import React, { forwardRef, type ReactNode } from "react";

import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "src/shadcn/lib/utils";
import { renderIcon, type IconProp } from "src/shared/renderIcon";
import { Badge as PrimitiveBadge } from "src/primitives/Badge";

import { SIZE_CONFIG, INDICATOR_COLORS } from "./constants";

type BadgeVariant =
  | "default"
  | "secondary"
  | "destructive"
  | "outline"
  | "ghost"
  | "link"
  | "gray"
  | "gray-subtle"
  | "blue"
  | "blue-subtle"
  | "purple"
  | "purple-subtle"
  | "amber"
  | "amber-subtle"
  | "red"
  | "red-subtle"
  | "pink"
  | "pink-subtle"
  | "green"
  | "green-subtle"
  | "teal"
  | "teal-subtle";

type BadgeSize = "sm" | "default" | "lg";

type BadgeShape = "pill" | "rounded";

export type IndicatorVariant =
  | "default"
  | "secondary"
  | "destructive"
  | "blue"
  | "green"
  | "amber"
  | "red"
  | "purple"
  | "pink"
  | "teal"
  | "gray";

export interface BadgeProps extends Omit<
  React.ComponentProps<"span">,
  "children"
> {
  /** Visual variant of the badge. */
  variant?: BadgeVariant;
  /** Size of the badge. */
  size?: BadgeSize;
  /** Corner shape: `pill` (fully rounded, default) or `rounded` (4px). */
  shape?: BadgeShape;
  /** Text label rendered inside the badge. */
  label?: string;
  /** Icon component, element, or class-name string. */
  icon?: IconProp;
  /** Placement of the icon relative to the label. */
  iconPosition?: "left" | "right";
  /** Colored status indicator dot. */
  indicator?: IndicatorVariant;
  /** Callback when the badge is dismissed. Renders a dismiss button when provided. */
  onDismiss?: () => void;
  /** Disables the badge and hides the dismiss button. */
  disabled?: boolean;
  /** Render as a custom element (polymorphic via Radix Slot). */
  asChild?: boolean;
  /** Children rendered inside the badge (fallback for `label`). */
  children?: ReactNode;
}

const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      variant = "default",
      size = "default",
      shape = "pill",
      label,
      icon,
      iconPosition = "left",
      indicator,
      onDismiss,
      disabled = false,
      className,
      asChild = false,
      children,
      ...otherProps
    },
    ref
  ) => {
    const { t } = useTranslation();

    if (asChild) {
      return (
        <PrimitiveBadge
          ref={ref}
          asChild
          variant={variant}
          size={size}
          shape={shape}
          className={className}
          {...otherProps}
        >
          {children}
        </PrimitiveBadge>
      );
    }

    const renderLabel = label || children;
    const iconElement = icon ? renderIcon(icon) : null;

    const hasDismiss = !!onDismiss && !disabled;

    return (
      <PrimitiveBadge
        ref={ref}
        variant={variant}
        size={size}
        shape={shape}
        className={cn(
          disabled && "opacity-50 pointer-events-none",
          icon && iconPosition === "left" && SIZE_CONFIG[size].iconPl,
          icon && iconPosition === "right" && SIZE_CONFIG[size].iconPr,
          hasDismiss && SIZE_CONFIG[size].dismissPr,
          className
        )}
        {...otherProps}
      >
        {indicator && (
          <span
            aria-hidden="true"
            className={cn(
              "size-1.5 shrink-0 rounded-full",
              INDICATOR_COLORS[indicator]
            )}
          />
        )}
        {iconPosition === "left" && iconElement}
        {renderLabel}
        {iconPosition === "right" && iconElement}
        {onDismiss && !disabled && (
          <button
            type="button"
            aria-label={t("neetoatoms.badge.dismiss", "Dismiss")}
            onClick={onDismiss}
            className={cn(
              "inline-flex shrink-0 items-center justify-center rounded-full bg-current/15 transition-[background-color,opacity] hover:bg-current/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
              SIZE_CONFIG[size].btnSize
            )}
          >
            <X className={SIZE_CONFIG[size].iconSize} />
          </button>
        )}
      </PrimitiveBadge>
    );
  }
);

Badge.displayName = "Badge";

export { Badge };
